import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { EnvelopeIcon, UserCircleIcon, ShieldCheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ALL_PAGE_PERMISSIONS } from "../auth/adminAccess";

const ALL_PERMISSIONS = ALL_PAGE_PERMISSIONS;

interface AdminProfile {
    uid: string;
    firstName: string;
    surname: string;
    email: string;
    jobTitle: string;
    department: string;
    permissions: string[];
    superAdmin: boolean;
}

interface PermissionsModalProps {
    admin: AdminProfile;
    isSelf: boolean;
    onClose: () => void;
    onSave: (uid: string, permissions: string[], superAdmin: boolean) => Promise<void>;
}

const PermissionsModal: React.FC<PermissionsModalProps> = ({ admin, isSelf, onClose, onSave }) => {
    const [permissions, setPermissions] = useState<string[]>(admin.permissions);
    const [superAdmin, setSuperAdmin] = useState(admin.superAdmin);
    const [saving, setSaving] = useState(false);

    const fullName = `${admin.firstName} ${admin.surname}`.trim() || admin.email;

    const toggle = (key: string) => {
        setPermissions((prev) =>
            prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(admin.uid, permissions, superAdmin);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
                    <div>
                        <h2 className="text-base font-semibold">Edit Permissions</h2>
                        <p className="text-xs text-gray-500 mt-0.5">{fullName}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
                    {/* Super Admin toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div>
                            <div className="text-sm font-semibold">Super Admin</div>
                            <div className="text-xs text-gray-500 mt-0.5">Full access to all pages and settings</div>
                        </div>
                        <button
                            onClick={() => setSuperAdmin((v) => !v)}
                            disabled={isSelf}
                            title={isSelf ? "You cannot change your own super admin status" : undefined}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${
                                superAdmin ? "bg-gray-800" : "bg-gray-200"
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    superAdmin ? "translate-x-6" : "translate-x-1"
                                }`}
                            />
                        </button>
                    </div>

                    {/* Page permissions */}
                    <div className="flex flex-col gap-1">
                        <div className="text-xs font-medium text-gray-500 uppercase mb-1">Page Access</div>
                        {ALL_PERMISSIONS.map(({ key, label }) => {
                            const enabled = superAdmin || permissions.includes(key);
                            return (
                                <div
                                    key={key}
                                    className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    <span className={`text-sm ${superAdmin ? "text-gray-400" : "text-gray-700"}`}>
                                        {label}
                                    </span>
                                    <button
                                        onClick={() => !superAdmin && toggle(key)}
                                        disabled={superAdmin}
                                        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:cursor-not-allowed ${
                                            enabled ? "bg-gray-800" : "bg-gray-200"
                                        }`}
                                    >
                                        <span
                                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                                enabled ? "translate-x-5" : "translate-x-0.5"
                                            }`}
                                        />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex gap-2 px-5 py-4 border-t shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || isSelf}
                        className="flex-1 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50"
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminsPage: React.FC = () => {
    const { currentUser } = useAuth();
    const [admins, setAdmins] = useState<AdminProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingAdmin, setEditingAdmin] = useState<AdminProfile | null>(null);

    useEffect(() => {
        document.title = "Admins - Peak^ Admin";

        const load = async () => {
            try {
                const snapshot = await getDocs(collection(db, "employees"));

                const profiles: AdminProfile[] = snapshot.docs.map((employeeDoc) => {
                    const uid = employeeDoc.id;
                    const data = employeeDoc.data();
                    return {
                        uid,
                        firstName: data.firstName ?? "",
                        surname: data.surname ?? "",
                        email: data.email ?? "",
                        jobTitle: data.jobTitle ?? "",
                        department: data.department ?? "",
                        permissions: Array.isArray(data.permissions) ? data.permissions : [],
                        superAdmin: data.superAdmin === true,
                    };
                });

                setAdmins(
                    profiles.sort((a, b) =>
                        `${a.firstName} ${a.surname}`.localeCompare(`${b.firstName} ${b.surname}`)
                    )
                );
            } catch (error) {
                console.error("Error loading admins:", error);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, []);

    const handleSavePermissions = async (uid: string, permissions: string[], superAdmin: boolean) => {
        if (!currentUser || uid === currentUser.uid) {
            throw new Error('You cannot change your own admin grants');
        }
        await updateDoc(doc(db, "employees", uid), { permissions, superAdmin });
        setAdmins((prev) =>
            prev.map((a) => (a.uid === uid ? { ...a, permissions, superAdmin } : a))
        );
    };

    return (
        <DashboardWrapper>
            <div className="w-full h-[calc(100vh-64px)] overflow-hidden p-6 flex flex-col gap-4">

                <div className="flex items-center justify-between shrink-0">
                    <div>
                        <h1 className="text-2xl font-heading">Admin Management</h1>
                        {!loading && (
                            <p className="text-sm text-gray-500 mt-0.5">
                                {admins.length} admin{admins.length !== 1 ? "s" : ""}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0">
                        <div className="col-span-4">Name</div>
                        <div className="col-span-3">Email</div>
                        <div className="col-span-2">Job Title</div>
                        <div className="col-span-2">Access</div>
                        <div className="col-span-1"></div>
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-0">
                        {loading ? (
                            <div className="flex items-center justify-center h-32">
                                <span className="loading loading-spinner loading-sm" />
                            </div>
                        ) : admins.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-32 text-gray-400 gap-2">
                                <UserCircleIcon className="w-10 h-10 text-gray-300" />
                                <p className="text-sm">No admins found.</p>
                            </div>
                        ) : (
                            admins.map((admin) => {
                                const fullName = `${admin.firstName} ${admin.surname}`.trim() || "—";
                                const initials = [admin.firstName, admin.surname]
                                    .filter(Boolean)
                                    .map((s) => s.charAt(0).toUpperCase())
                                    .join("");
                                const isMe = admin.uid === currentUser?.uid;

                                return (
                                    <div
                                        key={admin.uid}
                                        className="grid grid-cols-12 gap-4 px-6 py-3.5 border-b last:border-b-0 items-center hover:bg-gray-50 transition-colors"
                                    >
                                        {/* Avatar + name */}
                                        <div className="col-span-4 flex items-center gap-3 min-w-0">
                                            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                                                {initials || "?"}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                                                    {fullName}
                                                    {isMe && (
                                                        <span className="text-[10px] font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded-full shrink-0">
                                                            You
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-gray-400 truncate font-mono">{admin.uid}</div>
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="col-span-3 min-w-0">
                                            {admin.email ? (
                                                <a
                                                    href={`mailto:${admin.email}`}
                                                    className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-primary transition-colors min-w-0"
                                                >
                                                    <EnvelopeIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                                    <span className="truncate">{admin.email}</span>
                                                </a>
                                            ) : (
                                                <span className="text-sm text-gray-400">—</span>
                                            )}
                                        </div>

                                        {/* Job Title */}
                                        <div className="col-span-2 text-sm text-gray-600 truncate">
                                            {admin.jobTitle || "—"}
                                        </div>

                                        {/* Access summary */}
                                        <div className="col-span-2">
                                            {admin.superAdmin ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium bg-gray-800 text-white">
                                                    <ShieldCheckIcon className="w-3 h-3" />
                                                    Super Admin
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-500">
                                                    {admin.permissions.length} page{admin.permissions.length !== 1 ? "s" : ""}
                                                </span>
                                            )}
                                        </div>

                                        {/* Edit button */}
                                        <div className="col-span-1 flex justify-end">
                                            <button
                                                onClick={() => setEditingAdmin(admin)}
                                                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                                title="Edit permissions"
                                            >
                                                <ShieldCheckIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {editingAdmin && (
                <PermissionsModal
                    admin={editingAdmin}
                    isSelf={editingAdmin.uid === currentUser?.uid}
                    onClose={() => setEditingAdmin(null)}
                    onSave={handleSavePermissions}
                />
            )}
        </DashboardWrapper>
    );
};

export default AdminsPage;
